import { Icon } from "@iconify/react/dist/iconify.js";
import { useEffect, useMemo, useRef, useState } from "react";
import { io } from "socket.io-client";
import { toast } from "react-toastify";
import Loader from "../../components/Loader/Loader";
import axios from "axios";
import { AI_CHATBOT_BASE_URL } from "../../utils/constant";

const formatTime = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getChatbotHeaders = () => {
  const token = sessionStorage.getItem("token");
  const headers = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

const chatbotGet = async (endpoint, params = {}) => {
  const response = await axios.get(`${AI_CHATBOT_BASE_URL}${endpoint}`, {
    params,
    headers: getChatbotHeaders(),
  });
  return response.data;
};

const chatbotPost = async (endpoint, data = {}) => {
  const response = await axios.post(`${AI_CHATBOT_BASE_URL}${endpoint}`, data, {
    headers: getChatbotHeaders(),
  });
  return response.data;
};

const getSocketBaseUrl = () => {
  if (!AI_CHATBOT_BASE_URL) return "";
  try {
    const url = new URL(AI_CHATBOT_BASE_URL, window.location.origin);
    return url.origin;
  } catch {
    return AI_CHATBOT_BASE_URL.replace(/\/$/, "");
  }
};

const normalizeConversation = (conversation, selectedId) => {
  if (!conversation?._id) return null;
  return {
    ...conversation,
    unreadCount: conversation._id === selectedId ? 0 : Number(conversation.unreadCount || 0),
  };
};

const CUSTOMER_AVATAR = "https://wow-dash.com/demo/assets/images/user.png";
const ADMIN_AVATAR = "https://wow-dash.com/demo/assets/images/user-grid/user-grid-img13.png";

const AiChatbotSupportLayer = () => {
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [endingChat, setEndingChat] = useState(false);
  const [startingChat, setStartingChat] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [search, setSearch] = useState("");
  const [usingMockData, setUsingMockData] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState(
    typeof window !== "undefined" && "Notification" in window
      ? Notification.permission
      : "unsupported"
  );

  const selectedIdRef = useRef(null);
  const socketRef = useRef(null);
  const messageEndRef = useRef(null);
  const hasShownSocketErrorRef = useRef(false);

  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  const scrollToBottom = () => {
    messageEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const upsertConversation = (incoming) => {
    setConversations((prev) => {
      const normalized = normalizeConversation(incoming, selectedIdRef.current);
      if (!normalized) return prev;
      const rest = prev.filter((item) => item._id !== normalized._id);
      return [normalized, ...rest];
    });
  };

  const notifyAdmin = (title, body) => {
    toast.info(body || title);
    if (
      typeof window !== "undefined" &&
      "Notification" in window &&
      Notification.permission === "granted"
    ) {
      // Browser notifications are useful when the admin panel is open in a background tab.
      new Notification(title, { body });
    }
  };

  const requestNotificationPermission = async () => {
    if (typeof window === "undefined" || !("Notification" in window)) {
      toast.info("Browser notifications are not supported in this browser.");
      return;
    }
    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      if (permission === "granted") {
        toast.success("Browser notifications enabled.");
      } else {
        toast.info("Browser notifications remain disabled.");
      }
    } catch {
      toast.error("Could not enable browser notifications.", { id: "admin-error" });
    }
  };

  const loadConversations = async ({ keepSelection = true, silent = false } = {}) => {
    if (!silent) setLoadingConversations(true);
    try {
      const result = await chatbotGet("/ai-chat-support/conversations");
      if (result?.success && Array.isArray(result.data)) {
        setUsingMockData(false);
        setConversations(
          result.data.map((item) => normalizeConversation(item, selectedIdRef.current)).filter(Boolean)
        );
        setSelectedId((prev) => {
          if (keepSelection && prev && result.data.some((item) => item._id === prev)) {
            return prev;
          }
          return result.data[0]?._id || null;
        });
        return;
      }
      setConversations([]);
      setUsingMockData(false);
      if (!silent && result?.errors?.[0]) {
        toast.error(result.errors[0], { id: "admin-error" });
      }
    } catch (error) {
      setConversations([]);
      setUsingMockData(false);
      if (!silent) {
        toast.error(
          error?.response?.data?.errors?.[0] ||
          error?.response?.data?.message ||
          "Failed to load conversations",
          { id: "admin-error" }
        );
      }
    } finally {
      if (!silent) setLoadingConversations(false);
    }
  };

  const loadMessages = async (conversationId, options = {}) => {
    const { silent = false } = options;
    if (!conversationId) {
      setMessages([]);
      return;
    }
    if (!silent) setLoadingMessages(true);
    try {
      const result = await chatbotGet("/ai-chat-support/messages", {
        conversationId,
      });
      if (result?.success && Array.isArray(result.data)) {
        setMessages(result.data);
        setUsingMockData(false);
        setConversations((prev) =>
          prev.map((item) =>
            item._id === conversationId ? { ...item, unreadCount: 0 } : item
          )
        );
        return;
      }
      setMessages([]);
      if (!silent && result?.errors?.[0]) {
        toast.error(result.errors[0], { id: "admin-error" });
      }
    } catch (error) {
      setMessages([]);
      if (!silent) {
        toast.error(
          error?.response?.data?.errors?.[0] ||
          error?.response?.data?.message ||
          "Failed to load messages",
          { id: "admin-error" }
        );
      }
    } finally {
      if (!silent) setLoadingMessages(false);
    }
  };

  useEffect(() => {
    loadConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadMessages(selectedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const socketBaseUrl = getSocketBaseUrl();
    const token = sessionStorage.getItem("token");
    if (!socketBaseUrl || !token) {
      return undefined;
    }

    const socket = io(socketBaseUrl, {
      transports: ["websocket", "polling"],
      autoConnect: true,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      setSocketConnected(true);
      hasShownSocketErrorRef.current = false;
      socket.emit("join_admin_room", { token });
    });

    socket.on("disconnect", () => {
      setSocketConnected(false);
    });

    socket.on("connect_error", () => {
      setSocketConnected(false);
      if (!hasShownSocketErrorRef.current) {
        hasShownSocketErrorRef.current = true;
        toast.warning("Live chat socket is disconnected. Polling fallback is active.");
      }
    });

    socket.on("error_message", (payload) => {
      if (payload?.error) {
        toast.error(payload.error, { id: "admin-error" });
      }
    });

    socket.on("new_escalation", (payload) => {
      const conversation = payload?.conversation;
      if (conversation?._id) {
        upsertConversation(conversation);
        setSelectedId((prev) => prev || conversation._id);
      }
      notifyAdmin(
        "New chatbot escalation",
        conversation?.lastMessage || payload?.user_message || "A customer needs live support."
      );
    });

    socket.on("customer_message", (payload) => {
      const ticketId = payload?.ticket_id;
      const incomingMessage = payload?.message;
      if (!ticketId || !incomingMessage) return;
      if (selectedIdRef.current === ticketId) {
        setMessages((prev) => {
          if (prev.some((item) => item._id === incomingMessage._id)) {
            return prev;
          }
          return [...prev, incomingMessage];
        });
      }
      notifyAdmin("Customer replied", incomingMessage.text || payload?.text || "New customer message");
    });

    socket.on("conversation_updated", (payload) => {
      const conversation = payload?.conversation;
      if (!conversation?._id) return;
      upsertConversation(conversation);
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      loadConversations({ keepSelection: true, silent: true });
      if (!socketConnected && selectedIdRef.current) {
        loadMessages(selectedIdRef.current, { silent: true });
      }
    }, socketConnected ? 30000 : 12000);

    return () => window.clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socketConnected]);

  const selectedConversation = useMemo(
    () => conversations.find((conversation) => conversation._id === selectedId),
    [conversations, selectedId]
  );

  const isConversationClosed = selectedConversation?.status === "closed";

  const filteredConversations = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter((conversation) => {
      return (
        (conversation.customerName || "").toLowerCase().includes(q) ||
        (conversation.email || "").toLowerCase().includes(q)
      );
    });
  }, [conversations, search]);

  const handleSend = async (event) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text || !selectedId || isConversationClosed) return;

    const optimistic = {
      _id: `local-${Date.now()}`,
      text,
      sender: "admin",
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    setDraft("");
    setSending(true);

    try {
      const result = await chatbotPost("/ai-chat-support/send", {
        conversationId: selectedId,
        message: text,
      });
      if (result?.success) {
        // toast.success(result?.message || "Message sent", { id: "admin-success" });
        if (Array.isArray(result.data)) {
          setMessages(result.data);
        }
        await loadConversations();
        return;
      }
      setMessages((prev) => prev.filter((item) => item._id !== optimistic._id));
      toast.error(result?.errors?.[0] || "Failed to send message", { id: "admin-error" });
    } catch (error) {
      setMessages((prev) => prev.filter((item) => item._id !== optimistic._id));
      toast.error(
        error?.response?.data?.errors?.[0] ||
        error?.response?.data?.message ||
        "Failed to send message",
        { id: "admin-error" }
      );
    } finally {
      setSending(false);
    }
  };

  const handleEndChat = async () => {
    if (!selectedId || isConversationClosed) return;
    setEndingChat(true);
    try {
      const result = await chatbotPost("/ai-chat-support/end", {
        conversationId: selectedId,
      });
      if (result?.success) {
        toast.success(result?.message || "Chat ended", { id: "admin-success" });
        await loadMessages(selectedId);
        await loadConversations();
        return;
      }
      toast.error(result?.errors?.[0] || "Failed to end chat", { id: "admin-error" });
    } catch (error) {
      toast.error(
        error?.response?.data?.errors?.[0] ||
        error?.response?.data?.message ||
        "Failed to end chat",
        { id: "admin-error" }
      );
    } finally {
      setEndingChat(false);
    }
  };

  const handleStartChat = async () => {
    if (!selectedId || !isConversationClosed) return;
    setStartingChat(true);
    try {
      const result = await chatbotPost("/ai-chat-support/start", {
        conversationId: selectedId,
      });

      if (result?.success) {
        toast.success(result?.message || "Escalation started", { id: "admin-success" });
        await loadMessages(selectedId);
        await loadConversations();
        return;
      }
      toast.error(result?.errors?.[0] || "Failed to start escalation", { id: "admin-error" });
    } catch (error) {
      toast.error(
        error?.response?.data?.errors?.[0] ||
        error?.response?.data?.message ||
        "Failed to start escalation",
        { id: "admin-error" }
      );
    } finally {
      setStartingChat(false);
    }
  };

  return (
    <div className="card h-100 p-0 radius-12 overflow-hidden">
      {(loadingConversations || loadingMessages) && <Loader />}
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div>
          <h6 className="fw-semibold mb-4">Live conversations</h6>
          <p className="text-sm text-secondary-light mb-0">
            Handle escalated chatbot sessions in real time and reply directly from the admin panel.
          </p>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span
            className={`badge px-12 py-6 radius-8 ${socketConnected
              ? "bg-success-focus text-success-600"
              : "bg-warning-focus text-warning-600"
              }`}
          >
            {socketConnected ? "Socket connected" : "Polling fallback"}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={requestNotificationPermission}
          >
            {notificationPermission === "granted"
              ? "Notifications on"
              : "Enable notifications"}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => loadConversations({ keepSelection: true })}
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="card-body p-0">
        <div className="chat-wrapper px-24 pb-24">
          <div className="chat-sidebar border radius-12 bg-base">
            <div className="chat-search">
              <Icon icon="ion:search-outline" className="icon text-secondary-light" />
              <input
                type="text"
                placeholder="Search customers..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                aria-label="Search conversations"
              />
            </div>
            <div className="chat-all-list">
              {filteredConversations.length === 0 ? (
                <div className="p-24 text-center text-secondary-light">
                  {usingMockData
                    ? "Demo mode is active until the chatbot API is connected."
                    : "No escalated conversations yet."}
                </div>
              ) : (
                filteredConversations.map((conversation) => (
                  <div
                    key={conversation._id}
                    className={`chat-sidebar-single ${selectedId === conversation._id ? "active border-primary-300 bg-primary-50" : ""
                      }`}
                    onClick={() => setSelectedId(conversation._id)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setSelectedId(conversation._id);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="img">
                      <img
                        src={CUSTOMER_AVATAR}
                        alt={conversation.customerName || "Customer"}
                      />
                    </div>
                    <div className="info">
                      <h6 className="text-md mb-4">{conversation.customerName || "Customer"}</h6>
                      <p className="mb-0 text-sm text-secondary-light text-truncate" style={{ maxWidth: 180 }}>
                        {conversation.lastMessage || conversation.email || "—"}
                      </p>
                      <p className="mb-0 text-xs text-secondary-light mt-4">
                        {conversation.online ? (
                          <span className="text-success-600">Customer online</span>
                        ) : (
                          "Customer offline"
                        )}
                      </p>
                    </div>
                    <div className="action text-end">
                      {conversation.unreadCount > 0 && (
                        <span className="badge bg-primary-600 text-white rounded-pill">
                          {conversation.unreadCount}
                        </span>
                      )}
                      <p className="text-xs text-secondary-light mb-0 mt-4">
                        {formatTime(conversation.lastMessageAt)}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="chat-main border radius-12 bg-base">
            {!selectedConversation ? (
              <div className="chat-empty">
                <div className="text-center text-secondary-light">
                  <Icon icon="mdi:chat-outline" className="icon text-4xl mb-16" />
                  <p className="mb-0">Select a conversation to start chatting.</p>
                </div>
              </div>
            ) : (
              <>
                <div className="chat-sidebar-single top-profile border-bottom">
                  <div className="img">
                    <img
                      src={CUSTOMER_AVATAR}
                      alt={selectedConversation.customerName || "Customer"}
                    />
                  </div>
                  <div className="info">
                    <h6 className="text-md mb-4">
                      {selectedConversation.customerName || "Customer"}
                    </h6>
                    <p className="mb-0 text-sm text-secondary-light">
                      {selectedConversation.email || "Guest session"}
                      {selectedConversation.online ? (
                        <span className="text-success-600 ms-8">● Customer live</span>
                      ) : socketConnected ? (
                        <span className="text-secondary-light ms-8">Admin connected</span>
                      ) : (
                        <span className="text-secondary-light ms-8">Polling</span>
                      )}
                    </p>
                  </div>
                  <div className="ms-auto d-flex align-items-center gap-2">
                    <span
                      className={`badge ${isConversationClosed
                        ? "bg-neutral-200 text-secondary-light"
                        : "bg-warning-focus text-warning-600"
                        }`}
                    >
                      {isConversationClosed ? "Closed" : "Escalated"}
                    </span>
                    {isConversationClosed ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-success"
                        onClick={handleStartChat}
                        disabled={startingChat}
                      >
                        {startingChat ? "Starting..." : "Start escalation"}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={handleEndChat}
                        disabled={endingChat}
                      >
                        {endingChat ? "Ending..." : "End chat"}
                      </button>
                    )}
                  </div>
                </div>

                <div className="chat-message-list" style={{ backgroundColor: "#f8fafc" }}>
                  {messages.length === 0 ? (
                    <div className="p-24 text-center text-secondary-light">
                      No messages in this thread yet.
                    </div>
                  ) : (
                    messages.map((message) => {
                      const isAdmin = message.sender === "admin";
                      const isBot = message.sender === "bot";
                      const isSystemNote =
                        isBot &&
                        (
                          String(message.text || "").toLowerCase().includes("you turned off live support") ||
                          String(message.text || "").toLowerCase().includes("chat ended")
                        );
                      const isLeft = !isAdmin;

                      if (isSystemNote) {
                        return (
                          <div key={message._id} className="d-flex justify-content-center my-8">
                            <span className="badge bg-neutral-200 text-secondary-light px-14 py-8 radius-8 fw-medium">
                              {message.text}
                            </span>
                          </div>
                        );
                      }

                      return (
                        <div
                          key={message._id}
                          className={`chat-single-message ${isLeft ? "left" : "right"}`}
                          style={{
                            justifyContent: isLeft ? "flex-start" : "flex-end",
                            alignItems: "flex-end",
                            gap: 8,
                            width: "100%",
                            marginTop: 10,
                          }}
                        >
                          {isLeft && (
                            <div className="img">
                              {isBot ? (
                                <div
                                  style={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: "50%",
                                    background: "#eef2ff",
                                    color: "#4f46e5",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: 700,
                                    fontSize: 12,
                                  }}
                                >
                                  AI
                                </div>
                              ) : (
                                <img
                                  src={CUSTOMER_AVATAR}
                                  alt="Customer"
                                  style={{ width: 40, height: 40, borderRadius: "50%" }}
                                />
                              )}
                            </div>
                          )}
                          <div
                            className="chat-message-content"
                            style={{
                              background: isAdmin ? "#1d4ed8" : "#ffffff",
                              color: isAdmin ? "#ffffff" : "#111827",
                              border: isAdmin ? "none" : "1px solid #e5e7eb",
                              borderRadius: 12,
                              padding: "10px 12px",
                              maxWidth: "min(72%, 680px)",
                              boxShadow: "0 1px 2px rgba(16,24,40,0.06)",
                            }}
                          >
                            <p className="mb-4 text-xs fw-semibold" style={{ opacity: 0.85 }}>
                              {isAdmin ? "Admin" : isBot ? "AI" : selectedConversation.customerName || "Customer"}
                            </p>
                            {isBot && (
                              <span className="badge bg-neutral-200 text-secondary-light mb-8">
                                AI
                              </span>
                            )}
                            <p className="mb-6">{message.text}</p>
                            <p className="chat-time mb-0" style={{ color: isAdmin ? "#dbeafe" : "#6b7280" }}>
                              {formatTime(message.createdAt)}
                            </p>
                          </div>
                          {!isLeft && (
                            <div className="img flex-shrink-0">
                              <img
                                src={ADMIN_AVATAR}
                                alt="Admin"
                                style={{ width: 40, height: 40, borderRadius: "50%" }}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                  <div ref={messageEndRef} />
                </div>

                <form
                  className="chat-message-box border-top bg-white"
                  onSubmit={handleSend}
                  style={{ flexWrap: "nowrap" }}
                >
                  <input
                    type="text"
                    className="form-control"
                    placeholder={
                      isConversationClosed
                        ? "Chat ended. User is back with the AI assistant."
                        : "Type your reply as admin..."
                    }
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    disabled={sending || isConversationClosed}
                  />
                  <div className="chat-message-box-action" style={{ flexShrink: 0 }}>
                    <button
                      type="submit"
                      className="btn btn-primary d-flex align-items-center gap-2"
                      disabled={sending || !draft.trim() || isConversationClosed}
                    >
                      <Icon icon="mdi:send" className="icon" />
                      Send
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiChatbotSupportLayer;
